import axios from 'axios';
import React, { useEffect, useState } from 'react';
import waitForAudioStream, { EventData } from '../../utils/ServerSideEventConsumer';
import './SegmentMenu.css';
import { SegmentType } from './types';

interface SegmentMenuProps {
  currentSegment: SegmentType;
  setCurrentSegment: (segment: SegmentType) => void;
  refresh: (segment: SegmentType) => Promise<void>;
}

interface RefreshButtonProps {
  segment: SegmentType;
  refresh: (segment: SegmentType) => Promise<void>;
}

const RefreshButton: React.FC<RefreshButtonProps> = ({ segment, refresh }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async (segment: SegmentType) => {
    setIsRefreshing(true);
    try {
      refresh(segment);
    } finally {
      //      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    const eventCallback = async (streamEvent: EventData) => {
      if (streamEvent.eventType === 'stream-ready') {
        setIsRefreshing(false);
      }
    }

    waitForAudioStream.registerCallback('stream-ready', eventCallback);
    return () => {
      waitForAudioStream.removeCallback('stream-ready', eventCallback);
    };
  }, []);

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        handleRefresh(segment);
      }}
      className="p-1 ml-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className={`h-8 w-8 ${isRefreshing ? 'refresh-spin' : ''}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
    </button>
  );
};

const SegmentMenu: React.FC<SegmentMenuProps> = ({
  currentSegment,
  setCurrentSegment,
  refresh
}) => {
  const segments = Object.values(SegmentType);

  const playSegment = async (segment: SegmentType) => {
    setCurrentSegment(segment);
    await axios.get(`${process.env.REACT_APP_API_URL}/refresh`, {
      withCredentials: true,
      params: {
        segment,
        forceUser: `${JSON.parse(process.env.REACT_APP_USER ? process.env.REACT_APP_USER : '')?.email}`,
      }
    });
  }

  return (
    <div className="w-full bg-gray-100 p-1 rounded-lg">
      <div className="relative flex">
        {segments.map((segment) => (
          <div
            key={segment}
            onClick={() => playSegment(segment as SegmentType)}
            className={`
              flex-1 px-4  text-sm font-medium cursor-pointer
              transition-all duration-200 ease-in-out
              relative z-10 h-full
              ${currentSegment === segment
                ? 'text-gray-900'
                : 'text-gray-400 hover:text-gray-700'
              }
            `}
          >

            <div className="flex items-center justify-between w-full h-full">
              <span className="relative z-10">
                {segment.charAt(0).toUpperCase() + segment.slice(1)}
              </span>
              <RefreshButton
                segment={segment}
                refresh={refresh}
              />
            </div>

            {currentSegment === segment && (
              <div
                className="absolute inset-0 bg-white rounded-md shadow-md"
                style={{
                  zIndex: -1,
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SegmentMenu;